import { Component, Element, Event, type EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import type { SearchTab } from '../search-tab/search-tab';
import type { TabNavbarClicked } from '../tabs-header/TabNavbarClicked';
import { queryString } from '../../querystring';
import type { IdentifyActiveTabEvent, TrackAnalyticsEvent } from './TrackAnalyticsEvent';
import { makeLogger } from '../../../logger/logger';
import type {DashboardTabViewV2} from '../search-tab/subcomponents/dashboard-tab-view/dashboard-tab-view-v2';
import { Strategies, StrategiesFactory } from '../../strategies/Strategies';
import {BASE_URLS, CONVERSATIONAL_SEARCH, HIDE_LABELS, TABS_TYPE} from '../../strategies/utils/variables';
import type { HideLabelsStrategyService } from '../../strategies/HideLabelsStrategyService';
import type { BaseUrlsStrategyService } from '../../strategies/BasePathStrategyService';
import type { ConversationalSearchStrategyService } from '../../strategies/ConversationalSearchStrategyService';
import type {UiaasSearchDataFlux} from './dataflux';
import type { labelProviderInterface } from '../../LabelProviderInterface';
import DataFlux from '../../../utils/libs/dataflux/dataflux';
import EventBus from '../../../utils/libs/eventbus/eventbus';
import type {TabsStrategyService} from '../../strategies/TabsStrategy';

const { info, debug, warn, err } = makeLogger({ pathToCurrentFile: 'components/result-page/search/search.tsx' });
const bindingToDomLog = makeLogger({
  pathToCurrentFile: 'components/result-page/search/search.tsx',
  loggersList: ['info'],
  suffix: 'binding to DOM'
});

@Component({
  tag: 'uiaas-search',
  styleUrl: 'search.scss',
  shadow: true,
})
export class SearchComponent {

  private strategies: Strategies;
    @Element() el;

    @Event({
      eventName: 'trackanalytics',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      trackanalytics: EventEmitter<TrackAnalyticsEvent>;

    @Event({eventName: 'search:allTabsFinished'})
      searchAllTabsFinished: EventEmitter<number>;

    @Event({eventName: 'search:identifyActiveTab'})
      identifyActiveTab: EventEmitter<IdentifyActiveTabEvent>;

    @Event({eventName: 'search:mainComponentMounted'})
      mainComponentMountedEmitter: EventEmitter;

    @Event({eventName: 'search:replaceTabs'})
      replaceTabsEmitter: EventEmitter<{tab: SearchTab & HTMLElement | DashboardTabViewV2 & HTMLElement, number: number}>;

    /**
     * Application name. Passed to the API. The API response might depend on it.
     */
    @Prop() readonly appName: string;

    /**
     * Brand name.
     */
    @Prop() readonly brand: string = 'se';

    /**
     * Country as per se.com/cc/ll default URL structure. API heavily depends on it.
     */
    @Prop() readonly country: string;
    /**
     * Language as per se.com/cc/ll default URL structure. API heavily depends on it.
     */
    @Prop() readonly language: string;

    @Prop() readonly singleApplication: boolean = true;



    /**
     *  Enables right to left view mode
     */
    @Prop() readonly rtl:boolean;

    @Prop()
    readonly showResultsCount: boolean = false;

    @Prop() readonly resultsHeaderType: 'section' | 'subsection' = 'section';


    /**
     * Shows the number of displayed results and term for selected tab (above search result list)
     */
    @Prop() readonly resultSummary: boolean = false;

    /**
     * Prevents from sending request by checking filters, while the previous request is not resolved
     */
    @Prop() readonly disableFiltersOnLoading: boolean = true;
    /**
     * Shows scroll to top button by default
     */
    @Prop() readonly scrollToTopBtn: boolean = true;

    /**
     * Shows compare feature bar
     */
    @Prop() readonly isCompareFeature: boolean = false;


    /**
     * Function that provides the labels. Must implement interface (name:str, fallback:str)->label:str
     * If not provided, standard one will be used.
     *
     * @example
     <pre><code>
     const search = document.getElementById("uiaas-search")
     const labels = {
    "query.results": "Results",
    "button.search": "Search!",
    "filter-by":"Filter by:",
    "searchForFAQDev.facet.video.false":"nope",
    "searchForFAQDev.facet.video.true":"yep"
  }
     search.labelProvider = function (name, fallback) {
    return labels[name] ?? fallback
  }
     </code></pre>
     * When loading the labels with AJAX:
     * @example
     <pre><code>
     fetch("http://se.com/labels/usen.json").then(r=>r.json)=>then(labels=>{
     search.labelProvider = function(name,fallback){
       return labels[name] || fallback;
     }
   })
     </code></pre>
     */
    @Prop({ mutable: true }) labelProvider?: labelProviderInterface;

    /**
     * How long to wait for results from tab's API before give up. Milliseconds.
     */
    @Prop() readonly timeout?: number = 5000;

    /**
     * Title for the Search UIAAS Component.
     */
    @Prop() readonly searchTitle?: string;

    /**
     * Element to bind to for getting Search Queries. You can choose not to declarativly set the query
     * and hook on existing search form. If none of (bindTo, bindToSelector) are set, the component will render its own search box
     */
    @Prop() readonly bindTo?: string;
    /**
     * If provided, together with bindTo, will bind the query refresh action. Defaults to:
     * input[type='search'],input[name='query'],input[name='q']
     */
    @Prop() readonly bindToSelector?: string;

    /**
     * How the tabs should be presented?
     */
    @Prop() readonly tabPresentation: 'primary' | 'secondary';


    @Prop() readonly hideNavigation: boolean = false;

    /**
     * Show the results counts on tab headers?
     */
    @Prop() readonly tabCounts: boolean = false;

    @Prop() readonly showTabIcon: boolean;
    @Prop() readonly autoSwitch: boolean;
    @Prop() readonly autoSwitchRedirectTab = 0;

    /**
     * Hook on the forward/back browser buttons? If you are handling the history states on your own, don't enable this feature.
     * Default=false for application. Enabled for Search application on se.com
     */
    @Prop() readonly hookHistory: boolean = false;

    /**
     * Notifying the user about server error, by displaying the info in the place of the results
     */
    @Prop() readonly notifyAboutServerError: boolean = false;

    /**
     * Displays loading spinner in the place of results during sending the request
     */
    @Prop() readonly resultLoadingSpinner: boolean = true;

    @Prop() readonly tabsLimit = 5;

    @Prop()
    readonly runAutocorrect: boolean = false;

    /**
     *  Enables scoring for SRT app
     */
    @Prop() readonly srt: boolean = false;

    @Prop()
    readonly apigeeAuth: string;
    @Prop()
    readonly apigeeTokenUrl: string;

    @Prop() readonly optimizedLoading?: boolean;

    /**
     * Overrides the default green color of the quartz component. Possible values: 'success', 'primary', 'secondary'.
     * Default value: 'success'
     */
    @Prop() readonly defaultQuartzColor : 'success'|'primary'|'secondary' = 'success';

    /**
     * Query to execute
     */
    @Prop({ attribute: 'query' }) readonly queryProp?: string;

    @State() query: string;

    @Watch('queryProp')
    setQuery(newValue: string) {
        info('setQuery', this.query, '=>', newValue);
        this.query = newValue;
    }

    @Watch('query')
    pushQueryToTabs(newValue: string) {
        debug('push to tabs', [this.tabs, newValue]);
        this.tabs.forEach((tab) => {
            tab.queryProp = newValue;
            debug(
              'uiaas-search: setting query for child tabs',
              {
                newQuery: newValue,
                name: tab.tabName,
              },
              tab
            );
        });
    }


    @Watch('query')
    historyHookAction(newState, oldState, trigger) {
        if (!this.hookHistory) return;
        const data = {};
        if (this.query) data['query'] = this.query;
        history.pushState(data, 'title 1', '?' + queryString(data));
    }

    @State()
      tabs: Array<SearchTab & HTMLElement | DashboardTabViewV2 & HTMLElement> = [];

    @Prop() readonly tabular: boolean = false;

    @Prop() readonly stickyScrollTopBottomValue?: number;
    @Prop() readonly stickyElementsBottomOffset?: number;

    private dataflux = new DataFlux<UiaasSearchDataFlux>({
      eventbus: new EventBus(),
      activeTab: 0
    });

    private boundedType;
    private boundedWith;

    private debounceTime: number = 1000;
    private pendingSearches = 0;
    /**
     * Handle for setTimeout used in debounce mechanism
     * @private
     */
    private debounce: NodeJS.Timeout;

    private predictiveHandler(e) {
        debug('predictiveHandler event fired', [this, e]);

        const { country, language, locale, mode, resultUrl, term } = e.detail;
        e.preventDefault();
        this.query = term;
    }

    private formHandler(e) {
        debug('formHandler event fired', [this, e]);

        const selector =
            this.bindToSelector ??
            'input[type=\'search\'],input[name=\'query\'],input[name=\'q\']';
        const el = (e.target as HTMLFormElement).querySelector(selector);
        try {
          info(el, e, e.target, this);

          this.query = (el as HTMLInputElement).value;
          e.preventDefault();
          return false;
        } catch (e) {
          err('Search UIAAS can\'t capture the query!', {
            selector: selector,
            el: el,
            this: this,
          });
        }
    }

    private formHandlerDelegate;
    private inputHandlerDelegate;
    private prevKeyword: string;

    private inputHandler(e) {
        debug(e, this);

        const newValue = (e.currentTarget as HTMLInputElement).value;
        if (newValue != this.prevKeyword) {
          this.query = newValue;
        }
    }

    componentWillLoad() {
        if (!this.labelProvider) {
          info('Assigning default labelProvider to Search component');

          this.labelProvider = (name, fallback) => {
              warn('LabelProvider not provided in Search component', [
                name,
                fallback,
              ]);
              return fallback;
          };
        }
        const labelProvider = this.labelProvider;

        const timeout = this.timeout;
        const country = this.country;
        const language = this.language;
        const appName = this.appName;
        const rtl = this.rtl;
        const notifyAboutServerError = this.notifyAboutServerError;
        const disableFiltersOnLoading = this.disableFiltersOnLoading;
        const resultLoadingSpinner = this.resultLoadingSpinner;
        const runAutocorrect = this.runAutocorrect;
        const apigeeAuth = this.apigeeAuth;
        const apigeeTokenUrl = this.apigeeTokenUrl;
        const srt = this.srt;
        const that = this;
        const autoSwitchRedirectTab = this.autoSwitchRedirectTab;
        const resultSummary = this.resultSummary;
        const optimizedLoading = this.optimizedLoading;
        const defaultQuartzColor = this.defaultQuartzColor;

        function scanForTabs(elements: HTMLCollection) {
            if (!elements || !elements.length) return;

            for (let i = 0; i < elements.length; i++) {
              const e = elements[i];
              const tagNames = ['uiaas-search-tab', 'dashboard-tab-view-v2'];

              if (!e || !e.tagName) continue;
              if (
                tagNames.includes(e.tagName.toLowerCase()) ||
                tagNames.includes(e.children[0]?.tagName.toLowerCase())
              ) {
                const searchTabs = tagNames.includes(e.tagName.toLowerCase()) ? [e as unknown as SearchTab | DashboardTabViewV2] : Array.from(e.children);

                searchTabs.map((searchTab, ) => {
                    that.tabs.push(searchTab);
                    setPropsForTab(searchTab, that.tabs.length - 1);
                });
              } else if (e && e.tagName) {
                scanForTabs(e.children);
              }
            }
        }

        function setPropsForTab(searchTab: SearchTab | DashboardTabViewV2, i: number) {
            const properties = {
              parentDataFlux: that.dataflux,
              labelProvider,
              timeout,
              country,
              language,
              appName,
              rtl,
              notifyAboutServerError,
              disableFiltersOnLoading,
              resultLoadingSpinner,
              runAutocorrect,
              apigeeAuth,
              apigeeTokenUrl,
              srt,
              tabNumber: i,
              autoSwitchRedirectTab,
              resultSummary,
              optimizedLoading,
              defaultQuartzColor,
              role: 'tabpanel',
            };

            for (const prop in properties) {
              if (!searchTab[prop]) {
                searchTab[prop] = properties[prop];
              }
            }
        }

        scanForTabs(this.el.children);



        // this.query is used internally, while queryProp is exposed to DOM.
        // And during first start, only queryProp is filled.
        // During fresh start the @Watch is not working yet, so we have to assign the value on our own.
        info('reading starting query', [this.query, this.queryProp]);

        if (this.queryProp) this.setQuery(this.queryProp);

        if(this.activeTabInitializeFromQuerystring) {
          this.setActiveTab(this.activeTabInitializeFromQuerystring);
        }


        this.strategies = StrategiesFactory.createStrategy(this.el);

        if(this.activeTabInitializeFromQuerystring) {
          const queryStringParamName = this.activeTabInitializeFromQuerystring;
          const urlParams = new URLSearchParams(window.location.search);
          const tabToOpen = urlParams.get(queryStringParamName);
          info('Active tab initizlied from querystring parameter',{
            activeTabInitializeFromQuerystring: this.activeTabInitializeFromQuerystring,
            tabToOpen
          });
          this.setActiveTab(tabToOpen);
        }
    }

    connectedCallback() {
        this.attachExternalEventListener();
    }

    disconnectedCallback() {
        this.detachExternalEventListener();
    }

    private attachExternalEventListener() {
        if (this.bindTo) {
          const e = document.querySelector(this.bindTo);
          if (
            e &&
                e.tagName.toLowerCase() == 'input' &&
                e.getAttribute('type').toLowerCase() == 'search'
          ) {
            bindingToDomLog.info('binding to change+keypress events', e);

            this.inputHandlerDelegate = (e) => this.inputHandler(e);
            e.addEventListener('change', this.inputHandlerDelegate);
            e.addEventListener('keypress', this.inputHandlerDelegate);
            this.boundedType = 'input';
            this.boundedWith = e;

            bindingToDomLog.info('bound to input on change', this.boundedWith);
          } else if (e && e.tagName.toLowerCase() == 'form') {
            this.boundedType = 'form';
            this.boundedWith = e;
            this.formHandlerDelegate = (e) => this.formHandler(e);
            e.addEventListener('submit', this.formHandlerDelegate);

            bindingToDomLog.info('bound to form on submit', this.boundedWith);
            // FIXME: integracja z form i text input box
          } else if (
            e &&
                e.tagName.toLowerCase() == 'uiaas-predictive-search'
          ) {
            this.boundedType = 'form';
            this.boundedWith = e;
            this.formHandlerDelegate = (e) => {
                bindingToDomLog.info('Predictive x UIAAS | event.preventDefault() ');
                this.predictiveHandler(e);
            };
            e.addEventListener('triggerSearch', this.formHandlerDelegate);
            bindingToDomLog.info('bound to UIAAS Predictive form on submit', this.boundedWith);
            // let ef = e.shadowRoot.querySelector("input")
            // this.inputHandlerDelegate = (ef) => this.inputHandler(ef);
            // ef.addEventListener("change", this.inputHandlerDelegate);
            // ef.addEventListener("keypress", this.inputHandlerDelegate);
            // this.boundedType = "input";
            // this.boundedWith = ef;
          } else {
            err(
              'Has problems with binding to the Search Form',
              [this.bindTo, e]
            );
          }
        }

    }

    private detachExternalEventListener() {
        if (this.boundedWith) {
          info('detaching event listeners', this.boundedWith);

          if (this.boundedType == 'form') {
            if (this.boundedWith && this.boundedWith.removeListener) {
              //check to avoid null references before removing listener
              this.boundedWith.removeListener(
                'submit',
                this.formHandlerDelegate
              );
            }
          } else if (this.boundedType.removeListener) {
            this.boundedWith.removeListener(
              'change',
              this.inputHandlerDelegate
            );
            this.boundedWith.removeListener(
              'keypress',
              this.inputHandlerDelegate
            );
            this.boundedWith.removeListener(
              'triggerSearch',
              this.inputHandlerDelegate
            );
          } else {
            info(
              'uiaas-search: detaching event listeners',
              this.boundedWith,
              ' has no method to detach event listener?'
            );
          }
        }
    }

    @Listen('click', { passive: false })
    handleNewTabClick(event:MouseEvent):void {
        const isOpenNewTab = event.composedPath().some(
          (anchor) => anchor instanceof HTMLAnchorElement && anchor.target === '_blank'
        );

        if (isOpenNewTab) {
          (document.activeElement as HTMLAnchorElement)?.blur();
        }
    }

    @Listen('popstate', { target: 'window' })
    async handleHardwareBackbutton(historyEvent: PopStateEvent) {
        info('history event state', historyEvent.state);
        if(historyEvent.state) {
          this.readState(historyEvent.state);
        }
    }

    private readState(data) {
        if (data.query && data.query != this.query) {
          this.query = data.query;
        }

        info('readState done!', data);
    }

    @Listen('submit', {
      capture: true,
      passive: false,
    })
    onSearch(e) {
        info('onSearch', e);
        this.query = e.currentTarget.querySelector('[type=search]').value;
        e.preventDefault();
        return false;
    }

    private searchForm() {
        return (
          <div>
            <form onSubmit={(e) => this.onSearch(e)}>
              <input type="search" name="search-box" />
              <button>
                {this.labelProvider('button.search', 'Search')}
              </button>
            </form>
          </div>
        );
    }

    @Listen('tabClicked')
    tabClicked(e: CustomEvent<TabNavbarClicked>) {
        this.dataflux.root.activeTab = e.detail.num;
        this.sendAnalytics({
          event: 'selectTab',
          tabName: e.detail.tab.tabName,
        });
    }

    @Method()
    async setActiveTab(i: number|string) {
        if(typeof i == 'string') {
          const parsed = Number(i);
          if(isNaN(parsed)) {
            //regular string, tab name was provided, we open indicated tab
            for(let nthTab = 0; nthTab < this.tabs.length; nthTab++) {
              if(this.tabs[nthTab].tabName == i) {
                this.dataflux.root.activeTab = nthTab;
                break;
              }
            }
          } else {
            //if number, that open the tab!
            //using the previous code.
            i = parsed;
          }
        }
        if(typeof i == 'number') {
          this.dataflux.root.activeTab = i;
        }
    }
    /**
     * If not set, then its not active. If set, UIAAS will open the given tab
     * on the component load, with name read from querystring.
     */
    @Prop() readonly activeTabInitializeFromQuerystring?: string;

    @Listen('search:completed')
    @Listen('search:started')
    @Listen('search:error')
    searchTabUpdated(event) {
        switch (event.type) {
        case 'search:started':
          this.pendingSearches++;
          break;
        case 'search:completed':
        case 'search:error':
          this.pendingSearches--;
          break;
        default:
          break;
        }
        if (this.debounce !== null) {
          clearTimeout(this.debounce);
          this.debounce = null;
        }

        this.debounce = setTimeout(() => {
            if (!this.pendingSearches) {
              const allResultSeTabData = this.reflectedTabs['se/all'];
              const allResultAPCTabData = this.reflectedTabs['apc/all'];
              if (allResultSeTabData?.count !== undefined) {
                this.searchAllTabsFinished.emit(allResultSeTabData?.count);
              }
              if (allResultAPCTabData?.count !== undefined) {
                this.searchAllTabsFinished.emit(allResultAPCTabData?.count);
              }
            }
            this.debounce = null;
        }, this.debounceTime);

        const newTabs = { ...this.reflectedTabs };

        const resolvedTab = newTabs[event.detail.tab];
        const count = resolvedTab?.count !== undefined && event.detail.count == undefined ? resolvedTab?.count : event.detail.count; //keep previous count on re-loading
        newTabs[event.detail.tab] = { ...event.detail, count};
        this.reflectedTabs = newTabs;


        /* eslint-disable no-useless-escape */
        const allResultsSearchTab = this.el?.querySelector(`[tab-name=\'${this.brand}/all\']`);
        const productsSearchTab = this.el?.querySelector(`[tab-name=\'${this.brand}/products\']`);
        const newAllResultsSearchTab = this.el?.querySelector(`[tab-name=\'${this.brand}/dashboard\']`);
        /* eslint-disable no-useless-escape */

        if (allResultsSearchTab) allResultsSearchTab.reflectedTabs = newTabs;
        if (newAllResultsSearchTab) newAllResultsSearchTab.reflectedTabs = newTabs;
        if (productsSearchTab) productsSearchTab.reflectedTabs = newTabs;
    }

    @Listen('search:completed')
    tabResultsCount(event) {
        const tabs = [...this.tabs.map((element) => (element.tabName))];
        if (this.autoSwitch && this.autoSwitchRedirectTab >= 0) {
          if (event.detail.tab == tabs[this.dataflux.root.activeTab] && event.detail.count === 0) {
            this.dataflux.root.activeTab = this.autoSwitchRedirectTab;
          }
        }
        this.identifyActiveTab.emit({'activeTab': tabs[this.dataflux.root.activeTab], 'count': event.detail?.count});
    }

    @Listen('search:countrySecectorEnabled')
    coutnrySelectorScrollHandler(event) {
        const bannerHeight = event.detail.height;
        const searchContainer = this.el.shadowRoot.querySelector('.search-element');

        searchContainer.style.marginTop = bannerHeight + 'px';
    }

    @State()
      reflectedTabs = {};
    @State()
      currentViewType: string;
    @Listen('viewType')
    onViewTypeChange(event) {
        this.currentViewType = event.viewType;
    }

    @Listen('uiaas:trackanalytics')
    ontrackanalytics(event) {
        this.sendAnalytics(event.detail);
    }

    /**
     * Enables global decommissioning country selector after loading
     *! Remove after global decommissioning
     */
    @State() countrySelectorVisibility = false;

    private sendAnalytics(details) {
        this.trackanalytics.emit(details);
    }

    private renderApp(children) {
        if (this.singleApplication) {
          return (
            <se-app option="website">
              <se-container
                color="standard"
                option="centered"
                padding="none"
              >
                <div id={'main'}>{children} </div>
              </se-container>
            </se-app>
          );
        } else return children;
    }

    componentDidLoad() {
        this.mainComponentMountedEmitter.emit();
    }

    render() {
        const hideLabelsStrategy:HideLabelsStrategyService = this.strategies.for(HIDE_LABELS);
        const isHideLabels = hideLabelsStrategy?.isHideLabels(this.query);
        const compareBaseUrlStrategy: BaseUrlsStrategyService = this.strategies.for(BASE_URLS);
        const compareBaseUrl = compareBaseUrlStrategy?.getBaseUrls()?.params?.compareBaseUrl;
        const myProductsCartUrl = compareBaseUrlStrategy?.getBaseUrls()?.params?.myProductsCartUrl;
        const conversationalSearchStrategy: ConversationalSearchStrategyService = this.strategies.for(CONVERSATIONAL_SEARCH);
        const conversationalSearchParams = conversationalSearchStrategy.getConversationalSearchConfiguration()?.params;
        const tabsStrategy:TabsStrategyService = this.strategies.for(TABS_TYPE);
        const TabsNavigation = tabsStrategy.makeTabs();
        const properties = tabsStrategy.getTabProps({});
        const activeTab = this.dataflux.root.activeTab;

        return (
          <Host>
            {this.renderApp([
              <div class="search-element">
                <slot name="above-tabs-top" />
                <slot name="search-results-header" >
                  <div class={{'autocorrect-srt--wrapper': this.srt && this.showResultsCount}}>
                    {this.showResultsCount && !isHideLabels &&
                      (<uiaas-search-results-header
                        query={this.query}
                        labelProvider={(e, f) => this.labelProvider(e, f)}
                        runAutocorrect={this.runAutocorrect}
                        activeTabData={
                          this.reflectedTabs[
                            this.tabs[activeTab]?.tabName
                          ]
                        }
                        disabled={!this.tabs[activeTab]?.getAttribute?.('run-autocorrect')}
                        resultsHeaderType={this.resultsHeaderType}
                      >
                      </uiaas-search-results-header>)}
                    <slot name="above-nav-header" />
                    <TabsNavigation
                      {...properties}
                      tabsPresentation={this.tabPresentation}
                      tabs={this.tabs}
                      tabsReflected={this.reflectedTabs}
                      active={activeTab}
                      hideNavigation={this.hideNavigation}
                      tab-counts={this.tabCounts}
                      showTabIcon={this.showTabIcon}
                      tabs-limit={this.tabsLimit}
                      rtl={this.rtl}
                      labelProvider={(e, f) => this.labelProvider(e, f)}
                      tabular={this.tabular}
                      query={this.query}
                    />
                  </div>
                </slot>
                <slot />
              </div>,
              this.scrollToTopBtn ? <svs-scroll-to-top-button /> : ''
            ])}
            <uiaas-my-products-notification-bar
              labelProvider={(e, f) => this.labelProvider(e, f)}
              country={this.country}
              language={this.language}
              term={this.queryProp}
              myProductsCartUrl={myProductsCartUrl ?? ''}
            />
            {this.isCompareFeature ? <uiaas-compare-bar
              labelProvider={(e, f) => this.labelProvider(e, f)}
              country={this.country}
              language={this.language}
              term={this.queryProp}
              redirectionBaseUrl={compareBaseUrl ?? ''}
              stickyScrollTopBottomValue={this.stickyScrollTopBottomValue}
              stickyElementsBottomOffset={this.stickyElementsBottomOffset}
            /> : ''}
            <uiaas-search-widget-conversational-search-chat-elements-wrapper
              labelProvider={(e, f) => this.labelProvider(e, f)}
              country={this.country}
              language={this.language}
              query={this.queryProp}
              apigeeAuth={this.apigeeAuth}
              apigeeTokenUrl={this.apigeeTokenUrl}
              conversationalSearchStrategyData={conversationalSearchParams}
            />
          </Host>
        );
    }
}
